import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faPrint, faUsers } from "@fortawesome/free-solid-svg-icons";
import { ModulePage } from "../../Global/ModulePage";
import GlobalDivTable from "../../Global/GlobalDivTable";
import GlobalPagination from "../../Global/GlobalPagination";
import BotonExportarGlobal from "../../Global/Botones/BotonExportarGlobal";
import ModalExportarGlobal from "../../Global/Modales/ModalExportarGlobal";
import logoIpetPdf from "../../../imagenes/logo_ipet50.png";
import ventasApi from "../api/ventasApi";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { escapeHtml, money, upper, yes } from "../ventasUtils";

export default function Planillas({ feedback, showFeedback }) {
  const [options, setOptions] = useState({ campanias: [], anios: [], divisiones: [], total_docentes: 0 });
  const [campaign, setCampaign] = useState("");
  const [type, setType] = useState("cursos");
  const [year, setYear] = useState("");
  const [division, setDivision] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Vincular los datos al filtro con el que fueron consultados evita mostrar
  // filas viejas mientras React inicia la siguiente petición.
  const [previewResult, setPreviewResult] = useState({ key: "", data: null });
  const [page, setPage] = useState(1);
  const previewBodyRef = useRef(null);

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  useEffect(() => {
    if (previewBodyRef.current) previewBodyRef.current.scrollTop = 0;
  }, [page, campaign, type, year, division]);

  const requestParams = useMemo(() => ({
    tipo: type,
    id_campania: campaign,
    id_anio: type === "cursos" ? year : "",
    id_division: type === "cursos" ? division : "",
  }), [type, campaign, year, division]);
  const requestKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);
  const currentRequestKeyRef = useRef(requestKey);
  currentRequestKeyRef.current = requestKey;
  const previewData = previewResult.key === requestKey ? previewResult.data : null;
  const isPreviewPending = loading || previewLoading || (Boolean(campaign) && previewResult.key !== requestKey);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await ventasApi.planillasOpciones();
      setOptions(data);
      setCampaign((current) => {
        if (current) return current;
        const active = data.campanias?.find((item) => yes(item.activo));
        return String(active?.id_campania || data.campanias?.[0]?.id_campania || "");
      });
    } catch (err) { showFeedback("error", err.message); }
    finally { if (!silent) setLoading(false); }
  }, [showFeedback]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => {
    load({ silent: true });
    if (campaign) {
      ventasApi.planillasDatos(requestParams)
        .then((data) => {
          // Un refresco anterior no debe reemplazar una planilla más reciente.
          if (currentRequestKeyRef.current === requestKey) {
            setPreviewResult({ key: requestKey, data });
          }
        })
        .catch(() => {});
    }
  }, 60000);

  useEffect(() => {
    let cancelled = false;
    if (!campaign) {
      setPreviewResult({ key: "", data: null });
      setPreviewLoading(false);
      return () => { cancelled = true; };
    }

    setPreviewLoading(true);
    ventasApi.planillasDatos(requestParams)
      .then((data) => {
        if (!cancelled) setPreviewResult({ key: requestKey, data });
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewResult({ key: requestKey, data: null });
          showFeedback("error", err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [campaign, requestKey, requestParams, showFeedback]);

  const fetchCurrentData = async () => {
    if (!campaign) {
      showFeedback("warning", "Seleccioná una campaña.");
      return null;
    }
    return ventasApi.planillasDatos(requestParams);
  };

  const openExport = async () => {
    setPreviewLoading(true);
    try {
      const data = await fetchCurrentData();
      if (!data) return;
      // La exportación se abre con un snapshot recién consultado. Así Excel/PDF,
      // vista previa e impresión parten de la misma información.
      setPreviewResult({ key: requestKey, data });
      if (!(data.items || []).length) {
        showFeedback("warning", "No hay registros para exportar con los filtros seleccionados.");
        return;
      }
      setExportData(data);
      setExportOpen(true);
    } catch (err) {
      showFeedback("error", err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeExport = () => {
    setExportOpen(false);
    setExportData(null);
  };

  const print = async () => {
    try {
      const data = await fetchCurrentData();
      if (!data) return;
      setPreviewResult({ key: requestKey, data });
      if (!(data.items || []).length) {
        showFeedback("warning", "No hay registros para imprimir con los filtros seleccionados.");
        return;
      }

      const popup = window.open("", "_blank", "width=1100,height=900");
      if (!popup) { showFeedback("error", "El navegador bloqueó la ventana de impresión."); return; }

      const campaignName = data.campania?.nombre || "VENTA ESCOLAR";
      const productName = data.campania?.producto_principal_nombre || "PRODUCTO";
      const price = Number(data.meta?.precio_unitario_referencia || 0);
      const priceLabel = data.meta?.precio_unitario_multiple ? "VARIOS" : (price > 0 ? money(price) : "");
      const currentYear = Number(data.meta?.anio_planilla || new Date().getFullYear());
      const botNumber = "3564 665050";
      const normalizeCourse = (value) => String(value || "").replace(/[°º]/g, "").trim();

      const courseSheet = (rows, courseYear, courseDivision) => {
        const courseLabel = `${courseYear || "SIN AÑO"} ${courseDivision || ""}`.trim();
        return `<section class="legacy-sheet">
          <div class="legacy-meta"><span>Curso: ${escapeHtml(courseLabel)} · Alumnos: ${rows.length}</span><strong>Número bot: ${botNumber}</strong><span>Docente responsable: _______________________________</span></div>
          <table class="legacy-grid legacy-grid--course">
            <colgroup><col class="c-name"><col class="c-dni"><col class="c-year"><col class="c-div"><col class="c-qty"><col class="c-qty"><col class="c-gain"><col class="c-paid"><col class="c-notes"></colgroup>
            <tbody>
              <tr><th class="legacy-school" colspan="9">I.P.E.T. Nº 50 &quot;Ing.Emilio F. Olmos&quot;</th></tr>
              <tr class="legacy-top"><th colspan="2">ALUMNOS</th><th colspan="6">${escapeHtml(String(campaignName).toLocaleUpperCase("es-AR"))}</th><th>${currentYear}</th></tr>
              <tr class="legacy-course-line"><th colspan="4">CURSO ${escapeHtml(courseLabel)}</th><th colspan="5">COMPLETAR A MANO</th></tr>
              <tr class="legacy-columns"><th rowspan="2">APELLIDO Y NOMBRES</th><th rowspan="2">DNI</th><th colspan="2">CURSO</th><th colspan="2">UNIDADES</th><th rowspan="2">GANANCIA<br>A PAGAR</th><th rowspan="2">IMPORTE<br>COBRADO</th><th rowspan="2">OBSERVACIONES</th></tr>
              <tr class="legacy-columns legacy-columns--sub"><th>AÑO</th><th>DIV.</th><th>VEND.</th><th>FALT.</th></tr>
              ${rows.map((row) => `<tr class="legacy-data-row"><td class="legacy-name">${escapeHtml(`${row.apellido || ""}${row.apellido && row.nombre ? ", " : ""}${row.nombre || ""}`)}</td><td>${escapeHtml(row.num_documento || "")}</td><td>${escapeHtml(normalizeCourse(courseYear))}</td><td>${escapeHtml(courseDivision || "")}</td><td></td><td></td><td></td><td></td><td></td></tr>`).join("")}
            </tbody>
          </table>
          <div class="legacy-total">TOTAL COBRADO: ______________</div>
        </section>`;
      };

      const teacherSheet = (rows, pageNumber, totalPages) => `<section class="legacy-sheet">
        <div class="legacy-meta"><span>Listado de docentes · Página ${pageNumber} de ${totalPages}</span><strong>Número bot: ${botNumber}</strong><span>Docentes en esta hoja: ${rows.length}</span></div>
        <table class="legacy-grid legacy-grid--teachers">
          <colgroup><col class="t-order"><col class="t-name"><col class="t-dni"><col class="t-qty"><col class="t-paid"><col class="t-notes"></colgroup>
          <tbody>
            <tr><th class="legacy-school" colspan="6">I.P.E.T. Nº 50 &quot;Ing.Emilio F. Olmos&quot;</th></tr>
            <tr class="legacy-top"><th colspan="2">PROFESORES / DOCENTES</th><th colspan="3">${escapeHtml(String(campaignName).toLocaleUpperCase("es-AR"))}</th><th>${currentYear}</th></tr>
            <tr class="legacy-course-line"><th colspan="3">PRODUCTO: ${escapeHtml(String(productName).toLocaleUpperCase("es-AR"))}</th><th colspan="3">PRECIO UNITARIO: ${priceLabel ? escapeHtml(priceLabel) : "____________"}</th></tr>
            <tr class="legacy-columns"><th>ORDEN</th><th>DOCENTE</th><th>DNI</th><th>CANT.</th><th>COBRADO</th><th>OBSERVACIONES</th></tr>
            ${rows.map((row, index) => `<tr class="legacy-data-row"><td>${((pageNumber - 1) * 42) + index + 1}</td><td class="legacy-name">${escapeHtml(row.nombre_completo || "")}</td><td>${escapeHtml(row.dni || "")}</td><td></td><td></td><td></td></tr>`).join("")}
          </tbody>
        </table>
        <div class="legacy-total">TOTAL COBRADO: ______________</div>
      </section>`;

      let body = "";
      if (type === "docentes") {
        const teachers = data.items || [];
        const chunks = [];
        for (let index = 0; index < teachers.length; index += 42) chunks.push(teachers.slice(index, index + 42));
        if (!chunks.length) chunks.push([]);
        body = chunks.map((rows, index) => teacherSheet(rows, index + 1, chunks.length)).join("");
      } else {
        const groups = new Map();
        (data.items || []).forEach((row) => {
          const courseYear = row.nombre_anio || "SIN AÑO";
          const courseDivision = row.nombre_division || "";
          const key = `${courseYear}|||${courseDivision}`;
          if (!groups.has(key)) groups.set(key, { courseYear, courseDivision, rows: [] });
          groups.get(key).rows.push(row);
        });
        body = Array.from(groups.values()).map((group) => courseSheet(group.rows, group.courseYear, group.courseDivision)).join("");
      }

      popup.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${escapeHtml(campaignName)} - Planillas</title><style>
        @page{size:A4 portrait;margin:5mm}
        *{box-sizing:border-box}
        html,body{margin:0;padding:0;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif}
        body{font-size:10px}
        .legacy-sheet{width:200mm;min-height:287mm;margin:0 auto;position:relative;break-after:page;page-break-after:always;padding-top:1mm}
        .legacy-sheet:last-child{break-after:auto;page-break-after:auto}
        .legacy-meta{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:4mm;height:8mm;font-size:8.5px;white-space:nowrap}
        .legacy-meta strong{text-align:center;font-size:8.5px}.legacy-meta span:last-child{text-align:right}
        .legacy-grid{width:100%;border-collapse:collapse;table-layout:fixed;border:1.2px solid #000}
        .legacy-grid th,.legacy-grid td{border:0.75px solid #000;padding:1.4mm 1.1mm;text-align:center;vertical-align:middle;line-height:1.05}
        .legacy-grid th{font-weight:700}
        .legacy-school{font-size:12px;height:7.2mm;letter-spacing:.05px}
        .legacy-top th{font-size:10px;height:6.7mm}
        .legacy-course-line th{font-size:8.5px;height:6.5mm}
        .legacy-columns th{font-size:8px;height:7mm}.legacy-columns--sub th{height:5.7mm;font-size:7.5px}
        .legacy-data-row td{height:5.2mm;font-size:7.6px;padding-top:.8mm;padding-bottom:.8mm}
        .legacy-data-row .legacy-name{text-align:left;font-weight:700;padding-left:1.3mm;white-space:nowrap;overflow:hidden;text-overflow:clip}
        .legacy-total{margin:4mm 0 0 auto;width:74mm;height:11mm;border:0.75px solid #000;display:flex;align-items:center;padding:0 3mm;font-size:8px;font-weight:700}
        .legacy-grid--course .c-name{width:58mm}.legacy-grid--course .c-dni{width:23mm}.legacy-grid--course .c-year{width:12mm}.legacy-grid--course .c-div{width:12mm}.legacy-grid--course .c-qty{width:13mm}.legacy-grid--course .c-gain{width:21mm}.legacy-grid--course .c-paid{width:21mm}.legacy-grid--course .c-notes{width:auto}
        .legacy-grid--course .legacy-data-row td{height:6mm;font-size:8px}
        .legacy-grid--teachers .t-order{width:11mm}.legacy-grid--teachers .t-name{width:80mm}.legacy-grid--teachers .t-dni{width:25mm}.legacy-grid--teachers .t-qty{width:17mm}.legacy-grid--teachers .t-paid{width:26mm}.legacy-grid--teachers .t-notes{width:auto}
        @media print{.legacy-sheet{margin:0;width:200mm;min-height:287mm}}
      </style></head><body>${body}<script>window.onload=()=>{window.focus();window.print();};</script></body></html>`);
      popup.document.close();
    } catch (err) { showFeedback("error", err.message); }
  };

  const previewItems = previewData?.items || [];
  // Paginación local: el servicio devuelve toda la planilla para que la
  // impresión y exportación sigan incluyendo todos los registros filtrados.
  const previewPageSize = 20;
  const previewTotalPages = Math.max(1, Math.ceil(previewItems.length / previewPageSize));
  const previewPage = Math.min(page, previewTotalPages);
  const previewStart = (previewPage - 1) * previewPageSize;
  const visiblePreviewItems = previewItems.slice(previewStart, previewStart + previewPageSize);
  const previewCampaignName = upper(previewData?.campania?.nombre || "", 150);
  const previewProductName = upper(previewData?.campania?.producto_principal_nombre || "", 150);

  const exportSourceData = exportOpen && exportData ? exportData : previewData;
  const exportItems = exportSourceData?.items || [];
  const exportCampaignName = upper(exportSourceData?.campania?.nombre || "", 150);
  const exportProductName = upper(exportSourceData?.campania?.producto_principal_nombre || "", 150);

  const exportSections = useMemo(() => {
    const rows = exportItems.map((row, index) => (type === "docentes"
      ? {
          orden: index + 1,
          docente: upper(row.nombre_completo || "", 180),
          dni: row.dni || "",
          cantidad: "",
          cobrado: "",
          observaciones: "",
        }
      : {
          alumno: upper(`${row.apellido || ""}${row.apellido && row.nombre ? ", " : ""}${row.nombre || ""}`, 180),
          dni: row.num_documento || "",
          anio: upper(row.nombre_anio || "", 80),
          division: upper(row.nombre_division || "", 80),
        }));

    return [{
      hoja: type === "docentes" ? "Docentes" : "Alumnos",
      titulo: exportCampaignName || "Planilla de ventas",
      subtitulo: exportProductName ? `Producto: ${exportProductName}` : "",
      columnas: type === "docentes"
        ? [
            { label: "Orden", key: "orden" },
            { label: "Docente", key: "docente" },
            { label: "DNI", key: "dni" },
            { label: "Cantidad", key: "cantidad" },
            { label: "Cobrado", key: "cobrado" },
            { label: "Observaciones", key: "observaciones" },
          ]
        : [
            { label: "Apellido y nombres", key: "alumno" },
            { label: "DNI", key: "dni" },
            { label: "Año", key: "anio" },
            { label: "División", key: "division" },
          ],
      registros: rows,
    }];
  }, [exportItems, exportCampaignName, exportProductName, type]);

  const exportSubtitle = useMemo(() => {
    const parts = [type === "docentes" ? "DOCENTES ACTIVOS" : "ALUMNOS ACTIVOS"];
    if (type === "cursos") {
      const selectedYear = (options.anios || []).find((item) => String(item.id_anio) === String(year));
      const selectedDivision = (options.divisiones || []).find((item) => String(item.id_division) === String(division));
      parts.push(selectedYear ? `AÑO ${upper(selectedYear.nombre_anio, 80)}` : "TODOS LOS AÑOS");
      parts.push(selectedDivision ? `DIVISIÓN ${upper(selectedDivision.nombre_division, 80)}` : "TODAS LAS DIVISIONES");
    }
    return parts.join(" · ");
  }, [division, options.anios, options.divisiones, type, year]);

  const exportFileName = useMemo(() => {
    const base = (exportCampaignName || previewCampaignName || "ventas")
      .toLocaleLowerCase("es-AR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return `planilla_${base || "ventas"}`;
  }, [exportCampaignName, previewCampaignName]);

  return (
    <ModulePage
      className="ventas-page ventas-page--planillas"
      title="Planillas de ventas"
      description="Consultá e imprimí planillas por curso o por docente."
      filters={[
        { key: "campania", type: "select", label: "Venta / campaña", value: campaign, onChange: resetPage(setCampaign), includeEmptyOption: false,
          options: [{ value: "", label: "SELECCIONAR..." }, ...(options.campanias || []).map((item) => ({ value: String(item.id_campania), label: `${upper(item.nombre, 150)}${yes(item.activo) ? " · ACTIVA" : " · DADA DE BAJA"}` }))] },
        { key: "tipo", type: "select", label: "Tipo de planilla", value: type, onChange: resetPage(setType), includeEmptyOption: false,
          options: [{ value: "cursos", label: "CURSOS Y ALUMNOS" }, { value: "docentes", label: "DOCENTES" }] },
        ...(type === "cursos" ? [
          { key: "anio", type: "select", label: "Año", value: year, onChange: resetPage(setYear), options: (options.anios || []).map((item) => ({ value: String(item.id_anio), label: upper(item.nombre_anio, 80) })), placeholder: "TODOS" },
          { key: "division", type: "select", label: "División", value: division, onChange: resetPage(setDivision), options: (options.divisiones || []).map((item) => ({ value: String(item.id_division), label: upper(item.nombre_division, 80) })), placeholder: "TODAS" },
        ] : []),
      ]}
      canCreate={false}
      headerActions={(
        <BotonExportarGlobal
          label="Exportar"
          className="ventas-headAction"
          onClick={openExport}
          disabled={loading || previewLoading || !campaign}
        />
      )}
      secondaryActions={[
        { key: "imprimir", label: "Imprimir", icon: faPrint, className: "mov-btn--primary", onClick: print, disabled: loading || previewLoading || !campaign },
      ]}
    >
      {/* Ajuste exclusivo de esta tabla: cuatro columnas, sin cambiar el CSS global. */}
      <style>{`.ventas-planillas-divTable .ventas-planillas-columns--alumnos {
        grid-template-columns: minmax(0, 3fr) minmax(0, 1fr) minmax(0, .65fr) minmax(0, .65fr);
      }`}</style>
      <div className={`ventas-planillas-panel${!campaign && !isPreviewPending ? " ventas-planillas-panel--empty" : ""}`}>
        <section className="ventas-planillas-tableCard" aria-label="Planilla de ventas">
          <div className="ventas-planillas-tableWrap">
            {!isPreviewPending && !campaign ? (
              <div className="ventas-planillas-empty">
                <FontAwesomeIcon icon={faFileLines} />
                <strong>Seleccioná una venta / campaña</strong>
                <span>La planilla aparecerá acá antes de imprimirla o exportarla.</span>
              </div>
            ) : !isPreviewPending && !previewItems.length ? (
              <div className="ventas-planillas-empty">
                <FontAwesomeIcon icon={faFileLines} />
                <strong>Sin registros para estos filtros</strong>
                <span>Cambiá el año o la división para completar la planilla.</span>
              </div>
            ) : (
              <GlobalDivTable
                className="ventas-global-table ventas-planillas-divTable has-bottom-pagination"
                gridClassName={`ventas-planillas-columns ${type === "docentes" ? "ventas-planillas-columns--docentes" : "ventas-planillas-columns--alumnos"}`}
                columns={type === "docentes" ? [
                  { label: "Orden", align: "center" }, "Docente", { label: "DNI", align: "center" },
                  { label: "Cant.", align: "center" }, { label: "Cobrado", align: "right" }, "Observaciones",
                ] : [
                  "Apellido y nombres", { label: "DNI", align: "center" },
                  { label: "Año", align: "center" }, { label: "Div.", align: "center" },
                ]}
                ariaLabel="Planilla de ventas"
                skeletonActionColumn={false}
                skeletonRows={8}
                loading={isPreviewPending}
                loadingLabel="Actualizando vista previa de la planilla"
                bodyRef={previewBodyRef}
              >
                {visiblePreviewItems.map((row, index) => type === "docentes" ? (
                  <div className="mov-gridTable mov-gridTable--row global-divTable__row entity-table-row ventas-planillas-columns ventas-planillas-columns--docentes" role="row" key={row.id_docente || `${row.dni}-${index}`}>
                    <div className="mov-gridCell is-center" role="cell">{previewStart + index + 1}</div>
                    <div className="mov-gridCell entity-main-cell" role="cell">{upper(row.nombre_completo || "", 180)}</div>
                    <div className="mov-gridCell is-center" role="cell">{row.dni || "—"}</div>
                    <div className="mov-gridCell is-center" role="cell">—</div>
                    <div className="mov-gridCell is-right" role="cell">—</div>
                    <div className="mov-gridCell" role="cell">—</div>
                  </div>
                ) : (
                  <div className="mov-gridTable mov-gridTable--row global-divTable__row entity-table-row ventas-planillas-columns ventas-planillas-columns--alumnos" role="row" key={row.id_alumno || `${row.num_documento}-${index}`}>
                    <div className="mov-gridCell entity-main-cell" role="cell">{upper(`${row.apellido || ""}${row.apellido && row.nombre ? ", " : ""}${row.nombre || ""}`, 180)}</div>
                    <div className="mov-gridCell is-center" role="cell">{row.num_documento || "—"}</div>
                    <div className="mov-gridCell is-center" role="cell">{upper(row.nombre_anio || "—", 80)}</div>
                    <div className="mov-gridCell is-center" role="cell">{upper(row.nombre_division || "—", 80)}</div>
                  </div>
                ))}
              </GlobalDivTable>
            )}
          </div>
          {campaign ? <GlobalPagination
            currentPage={previewPage}
            totalPages={previewTotalPages}
            totalRecords={isPreviewPending ? 0 : previewItems.length}
            from={!isPreviewPending && previewItems.length ? previewStart + 1 : 0}
            to={isPreviewPending ? 0 : Math.min(previewStart + previewPageSize, previewItems.length)}
            onPageChange={setPage}
            loading={isPreviewPending}
            showSummary={!isPreviewPending}
            itemLabel={type === "docentes" ? "docentes" : "alumnos"}
            ariaLabel="Paginación de planillas de ventas"
            compactPageItems
            className="ventas-tableFooter ventas-planillas-pagination"
            leftContent={isPreviewPending ? null : (
              <div className="ventas-planillas-preview">
                <span className="ventas-planillas-preview__icon" aria-hidden="true">
                  <FontAwesomeIcon icon={type === "docentes" ? faUsers : faFileLines} />
                </span>
                <div>
                  <strong>{`${previewItems.length} ${type === "docentes" ? "docentes" : "alumnos"} en la planilla`}</strong>
                  <span>{previewCampaignName ? `${previewCampaignName}${previewProductName ? ` · ${previewProductName}` : ""}` : "Seleccioná una venta para visualizar la planilla."}</span>
                </div>
              </div>
            )}
          /> : null}
        </section>
      </div>

      <ModalExportarGlobal
        open={exportOpen}
        title="Exportar planilla"
        subtitle="Elegí el formato para exportar la planilla filtrada."
        tituloArchivo={exportCampaignName ? `Planilla de ventas · ${exportCampaignName}` : "Planilla de ventas"}
        subtituloArchivoActual={exportSubtitle}
        nombreArchivo={exportFileName}
        logoPdfUrl={logoIpetPdf}
        seccionesActuales={exportSections}
        cantidadActual={exportItems.length}
        mostrarAlcanceTodos={false}
        alcanceActualLabel="Exportar planilla filtrada"
        alcanceActualDescription="Exporta todos los registros que coinciden con los filtros seleccionados."
        totalLabelSingular="registro en la planilla"
        totalLabelPlural="registros en la planilla"
        onClose={closeExport}
        onSuccess={(message) => showFeedback("success", message)}
        onError={(message) => showFeedback("error", message)}
      />

      {feedback}
    </ModulePage>
  );
}
